// src/shared/utils/utcRange.ts
//
// UTC calendar-day range math shared by the analytics and legacy-reports
// features (ADR-003 §4). Ranges are end-EXCLUSIVE instants in UTC epoch
// seconds: `from` = UTC midnight of the first day, `to` = UTC midnight AFTER
// the last day. The serving daily tables, coverage metadata, the product
// window T, and D+3 finalization are all UTC-day-grained, so presets and
// pickers operate on UTC dates (labeled "(UTC)" where days are shown).

import { DateTime } from 'luxon';

export const UTC_RANGE_PRESETS = [
  'today',
  'yesterday',
  'last7d',
  'last30d',
  'last90d',
  'currentMonth',
  'lastMonth',
  'ytd',
  'currentYear',
  'priorYear',
  'custom',
] as const;
export type UtcRangePreset = typeof UTC_RANGE_PRESETS[number];

/** Presets whose range derives from "now"; `custom` carries explicit from/to instead. */
export type ComputableUtcRangePreset = Exclude<UtcRangePreset, 'custom'>;

/** End-exclusive UTC epoch-second range. */
export interface UtcRange {
  from: number;
  to: number;
}

export const SECONDS_PER_DAY = 86_400;

/** Gateway cap for daily-aggregate and CSV ranges. */
export const MAX_RANGE_DAYS = 1095;

/** Gateway cap for minute-grained concurrency/heatmap ranges (presence retention). */
export const HEATMAP_MAX_RANGE_DAYS = 90;

/**
 * Compute a preset's end-exclusive UTC-day range at the given instant.
 * "Last N days" means the N UTC calendar days ending with today (inclusive).
 * When `floorSeconds` (the product-window date floor, UTC midnight of T) is
 * provided, `from` is clamped up to it.
 */
export function presetToRange(
  preset: ComputableUtcRangePreset,
  nowSeconds: number,
  floorSeconds?: number
): UtcRange {
  const dayStart = DateTime.fromSeconds(nowSeconds, { zone: 'utc' }).startOf('day');
  const tomorrow = dayStart.plus({ days: 1 });
  const monthStart = dayStart.startOf('month');
  const yearStart = dayStart.startOf('year');

  let range: UtcRange;
  switch (preset) {
    case 'today':
      range = { from: dayStart.toSeconds(), to: tomorrow.toSeconds() };
      break;
    case 'yesterday':
      range = { from: dayStart.minus({ days: 1 }).toSeconds(), to: dayStart.toSeconds() };
      break;
    case 'last7d':
      range = { from: dayStart.minus({ days: 6 }).toSeconds(), to: tomorrow.toSeconds() };
      break;
    case 'last30d':
      range = { from: dayStart.minus({ days: 29 }).toSeconds(), to: tomorrow.toSeconds() };
      break;
    case 'last90d':
      range = { from: dayStart.minus({ days: 89 }).toSeconds(), to: tomorrow.toSeconds() };
      break;
    case 'currentMonth':
      range = { from: monthStart.toSeconds(), to: monthStart.plus({ months: 1 }).toSeconds() };
      break;
    case 'lastMonth':
      range = { from: monthStart.minus({ months: 1 }).toSeconds(), to: monthStart.toSeconds() };
      break;
    case 'ytd':
      range = { from: yearStart.toSeconds(), to: tomorrow.toSeconds() };
      break;
    case 'currentYear':
      // Legacy parity: the legacy dashboard's "Current Year" spans the whole
      // calendar year (future days simply have no data).
      range = { from: yearStart.toSeconds(), to: yearStart.plus({ years: 1 }).toSeconds() };
      break;
    case 'priorYear':
      range = { from: yearStart.minus({ years: 1 }).toSeconds(), to: yearStart.toSeconds() };
      break;
  }

  if (floorSeconds !== undefined && range.from < floorSeconds) {
    range = { from: Math.min(floorSeconds, range.to), to: range.to };
  }
  return range;
}

/** Number of UTC calendar days covered by an end-exclusive day-aligned range. */
export function rangeDays(range: UtcRange): number {
  return Math.ceil((range.to - range.from) / SECONDS_PER_DAY);
}

/** Clamp a range's span to at most `maxDays`, keeping the (most recent) end fixed. */
export function clampRangeSpan(range: UtcRange, maxDays: number): UtcRange {
  if (rangeDays(range) <= maxDays) return range;
  return { from: range.to - maxDays * SECONDS_PER_DAY, to: range.to };
}

/**
 * Clamp a range's end down to an exclusive upper bound (`maxToSeconds`), keeping
 * at least one day. Used by Legacy Reports to enforce the ADR-011 query boundary:
 * a range extending past the boundary is truncated (never rejected); a range
 * entirely at/after the boundary collapses to the single last day before it.
 */
export function clampRangeToBoundary(range: UtcRange, maxToSeconds: number): UtcRange {
  if (range.to <= maxToSeconds) return range;
  const to = maxToSeconds;
  const from = Math.min(range.from, to - SECONDS_PER_DAY);
  return { from, to };
}

/** UTC midnight (epoch seconds) of a `YYYY-MM-DD` UTC calendar date; null if malformed. */
export function utcDateToSeconds(date: string): number | null {
  const dt = DateTime.fromFormat(date, 'yyyy-MM-dd', { zone: 'utc' });
  return dt.isValid ? dt.toSeconds() : null;
}
